import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnInit
} from '@angular/core';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {loadScreeningSettings} from '../../services/screening-settings/screening-settings.actions';
import {selectHasSettingByProjectId} from '../../services/screening-settings/screening-settings.selectors';
import {NgClass} from '@angular/common';
import {MatCardModule} from '@angular/material/card';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {ResearchPaperListComponent} from '../research-paper-list/research-paper-list.component';
import {
  checkScreeningRunningStart,
  startPaperResearch,
  startPaperScreening,
  stopScreeningStart
} from '../../services/research-paper/research-paper.actions';
import {selectSelectedProject} from '../../../project/service/project.selectors';
import {
  ResearchPaperUpdateDialogComponent
} from '../research-paper-update-dialog/research-paper-update-dialog.component';
import {ConfirmDialogComponent} from '../../../../utils/components/confirm-dialog/confirm-dialog.component';
import {Router, RouterLink} from '@angular/router';
import {
  isScreeningRunning,
  selectHasPapersByProjectId,
  selectPapersByProjectId
} from '../../services/research-paper/research-paper.selectors';
import {TourAnchorMatMenuDirective, TourService} from 'ngx-ui-tour-md-menu';
import {TOUR_STEPS_SCREENING} from '../../models/tour-steps';
import {ExportResultDialogComponent} from '../export-result-dialog/export-result-dialog.component';
import {loadScreeningCriterias} from '../../services/screening-criteria/screening-criteria.actions';
import {selectCriteriaByProjectId} from '../../services/screening-criteria/screening-criteria.selectors';
import {paperAuditedScreening} from '../../helper/research-paper';
import {ScreeningStartDialogComponent} from '../screening-start-dialog/screening-start-dialog.component';
import {ScreeningRunSettings} from '../../models/screening-settings';
import {
  hasPubmedAPIKey, hasScopusAPIKey,
  hasSemanticAPIKey
} from '../../../../utils/models/llm-setting';
import {
  LlmModelSettingDialogComponent
} from '../../../../utils/components/llm-model-setting-dialog/llm-model-setting-dialog.component';
import {selectKeywordsByProjectId} from '../../services/research-keyword/research-keyword.selectors';
import {ProjectPermissionService} from '../../../../utils/services/project-permission.service';
import {MatTooltip} from '@angular/material/tooltip';
import {LoadingCardComponent} from '../../../../utils/components/loading-card/loading-card.component';
import {EmptyStateComponent} from '../../../../utils/components/empty-state/empty-state.component';
import {HeaderComponent} from '../../../../utils/components/header/header.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {of} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {ResearchPaperDTO} from '../../dto/research-paper';
import {ScreeningCriteriaDTO} from '../../dto/screening-criteria';
import {ResearchKeywordDTO} from '../../dto/research-keywords';

@Component({
  selector: 'app-screening-list',
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressBarModule,
    ResearchPaperListComponent,
    RouterLink,
    NgClass,
    TourAnchorMatMenuDirective,
    MatTooltip,
    LoadingCardComponent,
    EmptyStateComponent,
    HeaderComponent
  ],
  templateUrl: './screening-list.component.html',
  styleUrl: './screening-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ScreeningListComponent implements OnInit, AfterViewInit {
  private readonly store: Store = inject(Store);
  private readonly router: Router = inject(Router);
  private readonly tourService = inject(TourService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly projectPermissionService: ProjectPermissionService = inject(ProjectPermissionService);

  private TOUR_KEY = 'screening-tour-completed';

  readonly project = this.store.selectSignal(selectSelectedProject);
  readonly projectId = computed(() => this.project()?.id);
  readonly hasSettings = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectHasSettingByProjectId(projectId)) : of(false))
    ),
    {initialValue: false}
  );
  readonly hasPapers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectHasPapersByProjectId(projectId)) : of(false))
    ),
    {initialValue: false}
  );
  readonly screeningRunning = this.store.selectSignal(isScreeningRunning);
  readonly criterias = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectCriteriaByProjectId(projectId)) : of([] as ScreeningCriteriaDTO[]))
    ),
    {initialValue: [] as ScreeningCriteriaDTO[]}
  );
  readonly keywords = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectKeywordsByProjectId(projectId)) : of([] as ResearchKeywordDTO[]))
    ),
    {initialValue: [] as ResearchKeywordDTO[]}
  );
  readonly papers = toSignal(
    toObservable(this.projectId).pipe(
      switchMap(projectId => projectId ? this.store.select(selectPapersByProjectId(projectId)) : of([] as ResearchPaperDTO[]))
    ),
    {initialValue: [] as ResearchPaperDTO[]}
  );

  readonly hasCriterias = computed(() => this.criterias().length > 0);
  readonly nextStepResearch = computed(() => this.papers().length === 0);
  readonly nextStepScreening = computed(() =>
    this.papers().length > 0 && this.papers().every(p => !paperAuditedScreening(p))
  );
  readonly noKeywords = computed(() => !this.projectId() || this.keywords().length === 0);
  readonly editMode = computed(() => this.projectPermissionService.hasWritePermission(this.project() ?? undefined));

  constructor() {
    effect(() => {
      const projectId = this.projectId();
      const keywords = this.keywords();
      if (!projectId || keywords.length === 0) {
        return;
      }
      this.store.dispatch(checkScreeningRunningStart({projectId}));
      this.store.dispatch(loadScreeningSettings({projectId}));
      this.store.dispatch(loadScreeningCriterias({projectId}));
    });
  }

  ngOnInit(): void {
    this.tourService.initialize(TOUR_STEPS_SCREENING, {
      enableBackdrop: true,
      backdropConfig: {
        offset: 10,
      },
    });
    this.tourService.end$.subscribe(() => {
      localStorage.setItem(this.TOUR_KEY, 'true');
    });
  }

  ngAfterViewInit() {
    if (!localStorage.getItem(this.TOUR_KEY)) {
      this.tourService.start();
    }
  }

  startResearch(): void {
    const projectId = this.projectId();
    if (!projectId) {
      console.log('No project id');
      return;
    }
    let warningMessage: string = '';
    if (!hasPubmedAPIKey()) {
      warningMessage += "You can provide an PubMed Api Key (optional).<br>"
    }
    if (!hasSemanticAPIKey()) {
      warningMessage += "You can provide an Semantic Scholar Api Key (optional).<br>"
    }
    if (!hasScopusAPIKey()) {
      warningMessage += "You don't provide an Scopus Api Key."
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Search for papers online',
        message: 'We will search online for papers based on your search strategy.<br>' +
          '  If you you want to revise the search-query, click at clicking on the settings icon next to the navigation item<br>' +
          'Iif you want to start the search, which can take a while click here?',
        warningMessage: warningMessage,
        warningExtendingText: "You have not optimal settings",
        warningButtonText: "Go to your settings",
        warningButtonClick: () => this.dialog.open(LlmModelSettingDialogComponent, {
          autoFocus: false,
          height: '600px',
          width: '600px',
          maxWidth: '100vw'
        }),
        confirmButtonText: 'Research',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.store.dispatch(startPaperResearch({projectId}));
    });

  }

  startScreening(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    if (!this.hasCriterias()) {
      this.dialog.open(ConfirmDialogComponent, {
        autoFocus: false,
        data: {
          title: 'No screening criteria',
          message: 'You have not set any screening criteria. Do you want to to go to the settings?',
          confirmButtonText: 'Proceed'
        }
      }).afterClosed().subscribe((result) => {
        if (!result) return;
        this.router.navigate(['/project', projectId])
      });
      return;
    }

    this.dialog.open(ScreeningStartDialogComponent,
      {autoFocus: false}
    ).afterClosed().subscribe((result?: ScreeningRunSettings) => {
      if (!result) return;
      this.store.dispatch(startPaperScreening({projectId, screeningRunSettings: result}));
    });

  }

  stopScreening(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(ConfirmDialogComponent, {
      autoFocus: false,
      data: {
        title: 'Stop Screening',
        message: 'Do you want to stop the screening process?',
        confirmButtonText: 'Stop'
      }
    }).afterClosed().subscribe((result) => {
      if (!result) return;
      this.store.dispatch(stopScreeningStart({projectId}));
    });
  }

  startExport(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(ExportResultDialogComponent, {
      autoFocus: false,
      data: {
        projectId
      }
    });
  }

  createAddPaperDialog(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }
    this.dialog.open(ResearchPaperUpdateDialogComponent, {
      width: '600px',
      height: '80vh',
      maxWidth: '100vw',
      autoFocus: false,
      data: {projectId}
    });
  }

}
